import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeft, ChevronRight, ChevronLeft, Check, AlertTriangle,
  Search, CheckCircle, XCircle, Radio, Plus, Minus, X, Clock
} from 'lucide-react'
import api, { getAllCattle } from '../../services/api'
import RfidScanModal from '../../components/RfidScanModal'
import { useAuth } from '../../contexts/AuthContext'

// =============================================================================
// STEP INDICATOR
// =============================================================================

function StepIndicator({ currentStep }) {
  const steps = ['Setup', 'Select Animals', 'Record Volumes', 'Results']
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
            i < currentStep
              ? 'bg-green-100 text-green-700'
              : i === currentStep
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-500'
          }`}>
            {i < currentStep ? (
              <Check className="h-4 w-4" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
            )}
            {label}
          </div>
          {i < steps.length - 1 && (
            <ChevronRight className="h-5 w-5 mx-1 text-gray-400" />
          )}
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// SESSION TIME TOGGLE
// =============================================================================

function SessionTimeToggle({ value, onChange }) {
  const options = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
    { value: 'Evening', label: 'Evening' },
  ]
  return (
    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            value === opt.value
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MilkSessionPage({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const [step, setStep] = useState(0)
  const [error, setError] = useState('')

  // Step 0: Session setup
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
  const [sessionTime, setSessionTime] = useState('AM')
  const [milkingMethod, setMilkingMethod] = useState('hand')
  const [notes, setNotes] = useState('')

  // Step 1: Animal selection
  const [selectedAnimalIds, setSelectedAnimalIds] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [scanModalOpen, setScanModalOpen] = useState(false)

  // Step 2: Volume recording
  const [volumes, setVolumes] = useState({}) // { animalId: { volume: number, duration: number } }

  // Step 3: Results
  const [results, setResults] = useState(null)

  // Fetch all active cattle
  const { data: cattleData, isLoading: cattleLoading } = useQuery({
    queryKey: ['cattle', ranchId, 'active'],
    queryFn: () => getAllCattle(ranchId, { status: 'active' }),
  })

  const allAnimals = useMemo(() => {
    if (!cattleData) return []
    return cattleData.items || cattleData || []
  }, [cattleData])

  // Filter to milking cows only
  const milkingCows = useMemo(() => {
    return allAnimals.filter(a => a.is_milking)
  }, [allAnimals])

  // Filtered animals for Step 1 (search). Cap the rendered subset at 200 so
  // 500-cow dairies stay responsive; users narrow via search or RFID scan.
  const DISPLAY_CAP = 200
  const filteredAnimals = useMemo(() => {
    let animals = milkingCows
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      animals = animals.filter(a =>
        (a.local_name && a.local_name.toLowerCase().includes(q)) ||
        (a.rfid_tag && a.rfid_tag.toLowerCase().includes(q)) ||
        (a.tag_id && a.tag_id.toLowerCase().includes(q))
      )
    }
    return animals
  }, [milkingCows, searchQuery])
  const displayedAnimals = useMemo(
    () => filteredAnimals.slice(0, DISPLAY_CAP),
    [filteredAnimals]
  )
  const filteredOverflow = filteredAnimals.length - displayedAnimals.length

  // Selected animals for Step 2
  const selectedAnimals = useMemo(() => {
    return allAnimals.filter(a => selectedAnimalIds.has(a.id))
  }, [allAnimals, selectedAnimalIds])

  // Total liters
  const totalLiters = useMemo(() => {
    return Object.values(volumes).reduce((sum, v) => sum + (parseFloat(v.volume) || 0), 0)
  }, [volumes])

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (data) => api.post('/dairy/milk-sessions', data).then(res => res.data),
    onSuccess: (data) => {
      setResults(data)
      setStep(3)
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Failed to record milk session')
    }
  })

  // Animal selection handlers
  const toggleAnimal = (animalId) => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      if (next.has(animalId)) {
        next.delete(animalId)
      } else {
        next.add(animalId)
      }
      return next
    })
  }

  const selectAllFiltered = () => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      filteredAnimals.forEach(a => next.add(a.id))
      return next
    })
  }

  const deselectAll = () => {
    setSelectedAnimalIds(new Set())
  }

  const handleAnimalScanned = (animal) => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      if (next.has(animal.id)) {
        next.delete(animal.id)
      } else {
        next.add(animal.id)
      }
      return next
    })
  }

  // Volume handlers
  const updateVolume = (animalId, field, value) => {
    setVolumes(prev => ({
      ...prev,
      [animalId]: {
        ...prev[animalId],
        [field]: value,
      }
    }))
  }

  const adjustVolume = (animalId, delta) => {
    setVolumes(prev => {
      const current = parseFloat(prev[animalId]?.volume) || 0
      const newVal = Math.max(0, Math.round((current + delta) * 10) / 10)
      return {
        ...prev,
        [animalId]: {
          ...prev[animalId],
          volume: newVal,
        }
      }
    })
  }

  // Initialize volumes when moving to step 2
  const goToRecordVolumes = () => {
    if (selectedAnimalIds.size === 0) {
      setError('Please select at least one animal')
      return
    }
    // Initialize volume entries for selected animals
    const newVolumes = { ...volumes }
    for (const id of selectedAnimalIds) {
      if (!newVolumes[id]) {
        newVolumes[id] = { volume: '', duration: '' }
      }
    }
    setVolumes(newVolumes)
    setStep(2)
    setError('')
  }

  // Submit
  const handleSubmit = () => {
    const animalsWithVolumes = selectedAnimals.filter(a => {
      const v = volumes[a.id]
      return v && parseFloat(v.volume) > 0
    })

    if (animalsWithVolumes.length === 0) {
      setError('Please record at least one volume')
      return
    }

    setError('')

    const payload = {
      ranch_id: ranchId,
      session_date: sessionDate,
      session_time: sessionTime,
      milking_method: milkingMethod,
      notes: notes || null,
      recorded_by: userId,
      animals: animalsWithVolumes.map(a => ({
        animal_id: a.id,
        rfid_tag: a.rfid_tag || null,
        volume_liters: parseFloat(volumes[a.id]?.volume) || 0,
        duration_minutes: volumes[a.id]?.duration ? parseFloat(volumes[a.id].duration) : null,
      })),
    }

    submitMutation.mutate(payload)
  }

  // Validation
  const canProceedStep0 = sessionDate && sessionTime && milkingMethod

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => step === 0 ? navigate('/dairy') : setStep(step - 1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Record Milk Session</h1>
          <p className="text-gray-600">Record milk volumes for individual cows</p>
        </div>
      </div>

      <StepIndicator currentStep={step} />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 0: SESSION SETUP */}
      {/* ================================================================= */}
      {step === 0 && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto space-y-6">
          {/* Session Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
            <input
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Session Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Time</label>
            <SessionTimeToggle value={sessionTime} onChange={setSessionTime} />
          </div>

          {/* Milking Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Milking Method</label>
            <select
              value={milkingMethod}
              onChange={(e) => setMilkingMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="hand">Hand Milking</option>
              <option value="machine">Machine Milking</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any notes about this milking session..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Next button */}
          <div className="flex justify-end">
            <button
              onClick={() => { setStep(1); setError('') }}
              disabled={!canProceedStep0}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Select Animals <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 1: ANIMAL SELECTION */}
      {/* ================================================================= */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Session summary bar */}
          <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{sessionDate}</span>
              {' '}&middot; {sessionTime}
              {' '}&middot; {milkingMethod === 'hand' ? 'Hand Milking' : 'Machine Milking'}
            </div>
            <div className="text-sm font-medium text-primary-700">
              {selectedAnimalIds.size} of {milkingCows.length} selected
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or RFID..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <button onClick={selectAllFiltered} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200">
                Select All ({filteredAnimals.length})
              </button>
              <button onClick={deselectAll} className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">
                Deselect All
              </button>
              <button
                onClick={() => setScanModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
              >
                <Radio className="h-4 w-4" />
                RFID Scan
              </button>
            </div>
          </div>

          {/* Animal list */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {cattleLoading ? (
              <div className="p-8 text-center text-gray-500">Loading cattle...</div>
            ) : milkingCows.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="font-medium">No milking cows found</p>
                <p className="text-sm mt-1">Mark cows as milking (is_milking=true) to see them here.</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left w-10"></th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">RFID</th>
                      <th className="px-4 py-3 text-right">Lactation #</th>
                      <th className="px-4 py-3 text-right">DIM</th>
                      <th className="px-4 py-3 text-right">Last Yield (L)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {displayedAnimals.map(animal => {
                      const isSelected = selectedAnimalIds.has(animal.id)
                      return (
                        <tr
                          key={animal.id}
                          onClick={() => toggleAnimal(animal.id)}
                          className={`cursor-pointer transition-colors ${
                            isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAnimal(animal.id)}
                              className="h-4 w-4 rounded text-emerald-600"
                            />
                          </td>
                          <td className="px-4 py-2 font-medium">{animal.local_name || animal.tag_id || '-'}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-500">{animal.rfid_tag || '-'}</td>
                          <td className="px-4 py-2 text-right">{animal.lactation_number ?? '-'}</td>
                          <td className="px-4 py-2 text-right">{animal.days_in_milk ?? '-'}</td>
                          <td className="px-4 py-2 text-right">
                            {animal.last_milk_yield != null
                              ? `${animal.last_milk_yield} L`
                              : '-'}
                          </td>
                        </tr>
                      )
                    })}
                    {filteredAnimals.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No animals match your search
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {filteredOverflow > 0 && (
                  <div className="px-4 py-3 bg-amber-50 border-t border-amber-200 text-sm text-amber-800 text-center">
                    Showing {displayedAnimals.length} of {filteredAnimals.length} milking cows.
                    Narrow your search or use RFID scan to reach the rest.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button onClick={() => setStep(0)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={goToRecordVolumes}
              disabled={selectedAnimalIds.size === 0}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Record Volumes ({selectedAnimalIds.size} cows) <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* RFID Scan Modal */}
          <RfidScanModal
            isOpen={scanModalOpen}
            onClose={() => setScanModalOpen(false)}
            onAnimalScanned={handleAnimalScanned}
            scanCount={selectedAnimalIds.size}
            ranchId={ranchId}
          />
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 2: RECORD VOLUMES */}
      {/* ================================================================= */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Session summary bar */}
          <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{sessionDate}</span>
              {' '}&middot; {sessionTime}
              {' '}&middot; {milkingMethod === 'hand' ? 'Hand Milking' : 'Machine Milking'}
              {' '}&middot; {selectedAnimals.length} cows
            </div>
            <div className="text-lg font-bold text-emerald-700">
              Total: {totalLiters.toFixed(1)} L
            </div>
          </div>

          {/* Volume entries */}
          <div className="space-y-3">
            {selectedAnimals.map(animal => {
              const vol = volumes[animal.id] || { volume: '', duration: '' }
              return (
                <div key={animal.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {animal.local_name || animal.tag_id || 'Unknown'}
                      </span>
                      {animal.rfid_tag && (
                        <span className="ml-2 text-xs font-mono text-gray-400">{animal.rfid_tag}</span>
                      )}
                    </div>
                    {animal.last_milk_yield != null && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Last: {animal.last_milk_yield} L
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Volume input with quick buttons */}
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Volume (liters)</label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => adjustVolume(animal.id, -0.5)}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600"
                          title="-0.5 L"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={vol.volume}
                          onChange={(e) => updateVolume(animal.id, 'volume', e.target.value)}
                          placeholder="0.0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={() => adjustVolume(animal.id, 0.5)}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600"
                          title="+0.5 L"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => adjustVolume(animal.id, 1)}
                          className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium"
                          title="+1 L"
                        >
                          +1L
                        </button>
                      </div>
                    </div>

                    {/* Duration input */}
                    <div className="w-32">
                      <label className="block text-xs text-gray-500 mb-1">Duration (min)</label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={vol.duration}
                          onChange={(e) => updateVolume(animal.id, 'duration', e.target.value)}
                          placeholder="--"
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Total bar */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
            <div className="text-sm text-emerald-700">
              {selectedAnimals.length} cows recorded
              {' '}&middot;{' '}
              {Object.values(volumes).filter(v => parseFloat(v.volume) > 0).length} with volumes
            </div>
            <div className="text-xl font-bold text-emerald-800">
              Total: {totalLiters.toFixed(1)} L
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending || totalLiters === 0}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitMutation.isPending ? (
                <>
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit {totalLiters.toFixed(1)} L
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 3: RESULTS */}
      {/* ================================================================= */}
      {step === 3 && results && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{results.total_cows ?? results.total_animals ?? 0}</div>
              <div className="text-sm text-gray-500">Total Cows</div>
            </div>
            <div className="bg-emerald-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-emerald-700">{results.matched ?? results.matched_animals ?? 0}</div>
              <div className="text-sm text-emerald-600">Matched</div>
            </div>
            <div className="bg-amber-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">{results.unmatched ?? results.unmatched_animals ?? 0}</div>
              <div className="text-sm text-amber-600">Unmatched</div>
            </div>
            <div className="bg-blue-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{(results.total_liters ?? 0).toFixed(1)} L</div>
              <div className="text-sm text-blue-600">Total Liters</div>
            </div>
          </div>

          {/* Per-animal results */}
          {results.results && results.results.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Volume (L)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {results.results.map((r, i) => (
                      <tr key={i} className={
                        r.status === 'unmatched' ? 'bg-amber-50' :
                        r.status === 'error' ? 'bg-red-50' : ''
                      }>
                        <td className="px-4 py-3">
                          {r.status === 'recorded' && <CheckCircle className="h-5 w-5 text-emerald-500" />}
                          {r.status === 'unmatched' && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                          {r.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{r.animal_name || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-500">{r.rfid_tag || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{r.volume_liters != null ? `${r.volume_liters} L` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.message || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Done button */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/dairy')}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
